import React, { useState } from "react";
import styled from "styled-components";
import toast from "react-hot-toast";

import MediaPreviewModal from "../media-preview/components/MediaPreviewModal";
import AttachmentFileCard from "../file-attachment/AttachmentFileCard";
import { getFileIconFromExtension } from "../file-icon/fileIcon";
import type { MediaType } from "../../types/media-preview";
import {
  downloadFile,
  DOWNLOAD_FAILED_MESSAGE,
} from "../../utils/downloadFile";
import { mediaKindOfUrl } from "./markdownMedia";
import { useEmbeddedMedia } from "./embeddedMedia";

/**
 * Markdown 本文に埋め込まれたアップロードファイル（`[📎 名前](S3 URL)`）を
 * クリックでプレビューできる形で表示する。
 *
 * 本文中ではメディア本体を一切読み込まず（`<video>` / PDF を描画しない）、
 * クリックしたときだけ MediaPreviewModal で再生・表示する。プレビューできない
 * 種別（zip 等）は元のファイル名でダウンロードする。
 * 修飾キー付き・中クリックは「新しいタブで開く」等のブラウザ既定に任せる。
 *
 * 表示は 2 種類（variant）:
 * - "chip"（既定）: 1 行の小さなチップ。表のセルのような狭い場所向け
 *   （タスクボード）。動画・PDF は EmbeddedMediaContext からサムネイル画像が
 *   得られればチップの代わりにそれを表示する（遅延読み込み）。
 * - "card": メッセージ添付（AttachmentFileCard）と同一のカード。共有ノート
 *   向け。右端のダウンロードアイコンはモーダルを開かず直接ダウンロードする。
 *   サムネイルは出さない。
 */

const ChipAnchor = styled.a.attrs({ className: "bodytext-11" })`
  /* Markdown コンテナの & a（soremo-blue / 下線）より強くするため && */
  && {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 100%;
    margin: 2px 0;
    padding: 2px 8px 2px 6px;
    vertical-align: middle;
    border: 1px solid var(--soremo-border);
    border-radius: 4px;
    background: var(--soremo-background);
    color: inherit;
    text-decoration: none;
    line-height: 1.5;
    cursor: pointer;
  }

  @media (hover: hover) {
    &&:hover {
      border-color: var(--soremo-blue);
    }
  }
`;

const ChipIcon = styled.span.attrs({
  className: "material-symbols-rounded",
  "aria-hidden": true,
})`
  font-size: 16px;
  color: var(--soremo-light-gray);
  flex-shrink: 0;
`;

const ChipName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* Markdown 本文の流れに置くための余白（メッセージ側のレイアウトには影響しない） */
const CardAnchor = styled(AttachmentFileCard)`
  margin: 4px 0;
`;

/** サムネイル表示（画像の大きさは置き場所の img スタイルに従う） */
const ThumbnailAnchor = styled.a`
  && {
    position: relative;
    display: inline-block;
    max-width: 100%;
    vertical-align: middle;
    line-height: 0;
    text-decoration: none;
  }

  & img {
    display: block;
    border: 1px solid var(--soremo-border);
    border-radius: 4px;
    background: var(--soremo-background);
  }
`;

/** サムネイル右下の種別バッジ（動画 = 再生、PDF = PDF） */
const ThumbnailBadge = styled.span.attrs({
  className: "material-symbols-rounded",
  "aria-hidden": true,
})`
  position: absolute;
  right: 4px;
  bottom: 4px;
  font-size: 20px;
  line-height: 1;
  color: #fff;
  text-shadow: 0 0 4px rgba(0, 0, 0, 0.6);
  pointer-events: none;
`;

/** MediaPreviewModal で開ける種別（開けなければ null = ダウンロード） */
const previewTypeOfUrl = (url: string): MediaType | null => {
  const kind = mediaKindOfUrl(url);
  if (kind) return kind;
  const path = url.split(/[?#]/)[0];
  return path.toLowerCase().endsWith(".pdf") ? "pdf" : null;
};

/**
 * モーダルはポータル描画だが、React のイベントはポータル越しに親へ伝わる。
 * セル側のクリック（編集開始）やホバー（画像拡大）を誤発火させないよう、
 * モーダル内のイベントはここで止める。
 */
const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation();

export interface MarkdownAttachmentPreviewProps {
  href: string;
  /** 表示・保存に使うファイル名 */
  fileName: string;
  /** chip = 狭い場所向けの 1 行チップ（既定）/ card = メッセージ添付と同じカード */
  variant?: "chip" | "card";
}

const MarkdownAttachmentPreview: React.FC<MarkdownAttachmentPreviewProps> = ({
  href,
  fileName,
  variant = "chip",
}) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const previewType = previewTypeOfUrl(href);
  const thumbnailUrl = useEmbeddedMedia(href)?.thumbnailUrl;
  const showThumbnail =
    variant === "chip" &&
    !!thumbnailUrl &&
    !thumbnailFailed &&
    (previewType === "video" || previewType === "pdf");

  const download = () => {
    void downloadFile(href, fileName).then((succeeded) => {
      if (!succeeded) toast.error(DOWNLOAD_FAILED_MESSAGE);
    });
  };

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (
      e.defaultPrevented ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey ||
      e.button !== 0
    ) {
      return;
    }
    e.preventDefault();
    if (previewType) {
      setIsPreviewOpen(true);
    } else {
      download();
    }
  };

  /** カードのダウンロードアイコン: モーダルを開かず直接ダウンロード */
  const handleDownloadIconClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault(); // <a> の既定遷移を止める
    e.stopPropagation(); // カード本体の handleClick（モーダル）を止める
    download();
  };

  return (
    <>
      {variant === "card" ? (
        <CardAnchor
          href={href}
          fileName={fileName}
          onClick={handleClick}
          onDownloadIconClick={handleDownloadIconClick}
        />
      ) : showThumbnail ? (
        <ThumbnailAnchor href={href} title={fileName} onClick={handleClick}>
          <img
            src={thumbnailUrl}
            alt={fileName}
            loading="lazy"
            decoding="async"
            onError={() => setThumbnailFailed(true)}
          />
          <ThumbnailBadge>
            {previewType === "video" ? "play_circle" : "picture_as_pdf"}
          </ThumbnailBadge>
        </ThumbnailAnchor>
      ) : (
        <ChipAnchor href={href} title={fileName} onClick={handleClick}>
          <ChipIcon>{getFileIconFromExtension(fileName || href)}</ChipIcon>
          <ChipName>{fileName}</ChipName>
        </ChipAnchor>
      )}
      {isPreviewOpen && previewType && (
        <span
          onClick={stopPropagation}
          onMouseOver={stopPropagation}
          onMouseOut={stopPropagation}
          onKeyDown={stopPropagation}
        >
          <MediaPreviewModal
            src={href}
            mediaType={previewType}
            fileName={fileName}
            onClose={() => setIsPreviewOpen(false)}
            onDownload={download}
            downloadUrl={href}
          />
        </span>
      )}
    </>
  );
};

export default MarkdownAttachmentPreview;
